import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import ContextMenuItem from '@/modules/base/components/context-menu/context-menu-item/context-menu-item'
import { SquareIconButton } from '@/modules/base/components/buttons/index'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconIsShared from '@assets/images/icons/is_shared.svg'
import IconUnshare from '@assets/images/icons/cancel_sharing.svg'
import IconCopyLink from '@assets/images/icons/copy_link.svg'
import IconShare from '@assets/images/icons/share.svg'

import lang from '@/lang'
import { PropTypes } from 'prop-types'

import FileService from '@/modules/file/fileService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import IconEmail from '@assets/images/icons/email.svg'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import { updateSharedState } from '@/redux/ducks/document/list'
import SendEmailDrawer from './send-email-drawer'
import { mimeTypes } from '@/modules/base/util/constants'

const DocumentShareMenu = (props) => {
  const { file, onOpenEmailDrawer } = props
  const isPdf = file?.mimetype === mimeTypes.MIMETYPE_PDF
  const [isShared, setShared] = useState(file.isShared)
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false)
  const classes = useStyles(props)

  const unshare = async() => {
    const { meta: { isShared } } = await FileService.setShareHUBState(file.id, false)
    setShared(() => {
      updateSharedState(file.id, isShared)
      return isShared
    })
    toast.showToast({ message: lang.documents.sharingDisabled })
  }

  useEffect(() => {
    setShared(file.isShared)
  }, [file.id, file.isShared])

  const copyLink = () => {
    navigator.clipboard.writeText(file.shareHubUrl)
    toast.showToast({ message: lang.documents.shareHUBLinkCopied })
  }

  const shareFile = () => {
    FileService.setShareHUBState(file.id, true).then(data => {
      const { meta: { isShared } } = data
      setShared(() => {
        updateSharedState(file.id, isShared)
        return isShared
      })
    })
  }

  const UnshareMenuItem = (<ContextMenuItem
    label={lang.documents.disableSharing}
    icon={<SVGIcon svg={IconUnshare} width={'14px'} height={'14px'} />}
    onItemClick={unshare}
  />)
  const CopyLinkMenuItem = (<ContextMenuItem
    label={lang.documents.copyShareHUBLink}
    icon={<SVGIcon svg={IconCopyLink} width={'14px'} height={'14px'} />}
    onItemClick={copyLink}
  />)
  const SendEmailMenuItem = (<ContextMenuItem
    label={lang.documents.email.menuItem}
    icon={<SVGIcon svg={IconEmail} width={'14px'} height={'14px'} />}
    onItemClick={async() => {
      await onOpenEmailDrawer()
      setEmailDrawerOpen(true)
    }}
  />)

  const shareTooltipContent = (<div className={classes.shareTooltipContent}>
    <div className={classes.tooltipWrapper}>
      <div className={classes.shareTooltipHeader}>{lang.documents.enableSharingHeader}</div>
      <div className={classes.shareTooltipLabel}>{isPdf ? lang.documents.enableSharing : lang.documents.sharingNotAllowed}</div>
    </div>
  </div>)

  return (
    <div>
      <SendEmailDrawer open={emailDrawerOpen} onClose={() => setEmailDrawerOpen(false)} customerId={file.customer?.id} file={file} />
      {
        isShared
          ? <div>
            <SquareIconButton
              icon={IconIsShared}
              size='large'
              customClass={classes.button}
              popperStyle={{ popperContent: classes.popperContent }}
              autoClosePopper={true}
            >
              {UnshareMenuItem}
              {CopyLinkMenuItem}
              {SendEmailMenuItem}
            </SquareIconButton>
          </div>
          : <RichTooltip content={shareTooltipContent} width={280} placement='top'>
            <div>
              <SquareIconButton
                size='large'
                icon={IconShare}
                onClick={() => {
                  shareFile()
                  copyLink() // safari fix - The request is not allowed by the user agent
                }}
                disabled={!isPdf} />
            </div>
          </RichTooltip>
      }
    </div>

  )
}

const useStyles = makeStyles((theme) => ({
  shareTooltipContent: {
    display: 'flex',
    alignItems: 'center',
    color: theme.black,
  },
  shareTooltipHeader: {
    fontSize: theme.fontSizes.default,
    lineHeight: '20px',
    color: theme.black,
    fontWeight: theme.fontWeights.bold,
  },
  shareTooltipLabel: {
    fontSize: theme.fontSizes.smaller,
    marginTop: 20,
    '& strong': {
      fontWeight: theme.fontWeights.semibold,
    },
  },
  popperContent: {
    padding: '16px 0',
  },
  tooltipWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

DocumentShareMenu.propTypes = {
  file: PropTypes.object.isRequired,
  onOpenEmailDrawer: PropTypes.func,
}

export default DocumentShareMenu
